import { computed, inject, Type } from '@angular/core';
import { AccessPoint } from '@kinet-open-network/client/access-points/data-access';
import {
  apiFilter,
  ApiQuery,
} from '@kinet-open-network/client/core/api-client';
import { InvoicesService } from '@kinet-open-network/client/invoices/data-access';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withExport,
  withModelRestore,
  withResourceState,
} from '@kinet-open-network/client/shared/data-access/state';
import { Task } from '@kinet-open-network/client/tasks/data-access';
import {
  CustomerActivitySource,
  ICustomerActionData,
  ICustomerBankAccount,
  ICustomerBatchActionData,
  ICustomerStoryLog,
} from '@kinet-open-network/shared/data-access/model';
import {
  patchState,
  signalStore,
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { plainToInstance } from 'class-transformer';
import { firstValueFrom, Observable, switchMap } from 'rxjs';
import { CustomerBankAccount } from '../..';
import { SetAccessPointCloseDate } from '../models/api/set-close-date.model';
import { SetInvoiceBalanceData } from '../models/api/set-invoice-balance.model';
import { SetSipoInvoiceStateData } from '../models/api/set-sipo-invoice-state.model';
import { CustomerActivity } from '../models/customer-activity.model';
import { Customer } from '../models/customer.model';
import { CustomerBankAccountsService } from '../services/customer-bank-accounts.service';
import { CustomersService } from '../services/customers.service';

export interface CustomerAccessPointsStateSlice {
  activeAccessPointId: string | null;
  accessPoints: AccessPoint[];
  accessPointsLoading: boolean;
}

export const ACTIVE_ACCESSPOINT_STORAGE_KEY = 'activeApId';

export function withCustomerAccessPoints(
  Service: Type<{
    getAccessPoints: (customerId: number) => Observable<AccessPoint[]>;
    bindAccessPoint: (customerId: number, apId: string) => Observable<Customer>;
    unbindAccessPoint: (
      customerId: number,
      apId: string,
    ) => Observable<Customer>;
    closeAccessPoint: (
      customerId: number,
      apId: string,
      data: SetAccessPointCloseDate,
    ) => Observable<Customer>;
    changeAccessPoint: (
      customerId: number,
      apId: string,
      newApId: string,
    ) => Observable<Customer>;
    deleteAccessPoint: (
      customerId: number,
      apId: string,
    ) => Observable<Customer>;
  }>,
) {
  return signalStoreFeature(
    withState<CustomerAccessPointsStateSlice>({
      activeAccessPointId:
        localStorage.getItem(ACTIVE_ACCESSPOINT_STORAGE_KEY) ?? null,
      accessPoints: [],
      accessPointsLoading: false,
    }),
    withComputed(({ accessPoints, activeAccessPointId }) => ({
      activeAccessPoint: computed(() =>
        accessPoints().find((ap) => ap.id === activeAccessPointId()),
      ),
    })),
    withMethods((store, service = inject(Service)) => ({
      setActiveAccessPoint: (id: string | null) => {
        patchState(store, { activeAccessPointId: id });

        if (id) {
          localStorage.setItem(ACTIVE_ACCESSPOINT_STORAGE_KEY, id);
        } else {
          localStorage.removeItem(ACTIVE_ACCESSPOINT_STORAGE_KEY);
        }
      },
      loadAccessPoints: async (customerId?: number) => {
        patchState(store, { accessPointsLoading: true });
        if (!customerId) {
          patchState(store, {
            accessPointsLoading: false,
            accessPoints: [],
          });

          return [];
        }
        return callApiMethod(() => service.getAccessPoints(customerId), {
          next: (data) =>
            patchState(store, (state) => ({
              accessPoints: data,
              accessPointsLoading: false,
              activeAccessPointId:
                data.find((ap) => ap.id === state.activeAccessPointId)?.id ??
                data[0]?.id,
            })),
          error: () => patchState(store, { accessPointsLoading: false }),
        });
      },
      bindAccessPoint: async (customerId: number, apId: string) => {
        patchState(store, { accessPointsLoading: true });

        try {
          const accessPoints = await firstValueFrom(
            service
              .bindAccessPoint(customerId, apId)
              .pipe(switchMap(() => service.getAccessPoints(customerId))),
          );

          patchState(store, { accessPoints, activeAccessPointId: apId });
        } finally {
          patchState(store, { accessPointsLoading: false });
        }
      },
      unbindAccessPoint: async (customerId: number, apId: string) => {
        patchState(store, { accessPointsLoading: true });

        try {
          const accessPoints = await firstValueFrom(
            service
              .unbindAccessPoint(customerId, apId)
              .pipe(switchMap(() => service.getAccessPoints(customerId))),
          );

          patchState(store, {
            accessPoints,
            activeAccessPointId: apId,
          });
        } finally {
          patchState(store, { accessPointsLoading: false });
        }
      },
      closeAccessPoint: async (
        customerId: number,
        apId: string,
        data: SetAccessPointCloseDate,
      ) => {
        patchState(store, { accessPointsLoading: true });

        try {
          const accessPoints = await firstValueFrom(
            service
              .closeAccessPoint(customerId, apId, data)
              .pipe(switchMap(() => service.getAccessPoints(customerId))),
          );

          patchState(store, {
            accessPoints,
            activeAccessPointId: apId,
          });
        } finally {
          patchState(store, { accessPointsLoading: false });
        }
      },
      deleteAccessPoint: async (customerId: number, apId: string) => {
        patchState(store, { accessPointsLoading: true });

        try {
          const accessPoints = await firstValueFrom(
            service
              .deleteAccessPoint(customerId, apId)
              .pipe(switchMap(() => service.getAccessPoints(customerId))),
          );

          patchState(store, {
            accessPoints,
            activeAccessPointId: accessPoints[0]?.id ?? null,
          });
        } finally {
          patchState(store, { accessPointsLoading: false });
        }
      },
      changeAccessPoint: async (
        customerId: number,
        apId: string,
        newApId: string,
      ) => {
        patchState(store, { accessPointsLoading: true });

        try {
          const accessPoints = await firstValueFrom(
            service
              .changeAccessPoint(customerId, apId, newApId)
              .pipe(switchMap(() => service.getAccessPoints(customerId))),
          );

          patchState(store, { accessPoints });
        } finally {
          patchState(store, { accessPointsLoading: false });
        }
      },
    })),
  );
}

export interface CustomerStoriesStateSlice {
  stories: ICustomerStoryLog[];
  storiesLoading: boolean;
}

export function withCustomerStories() {
  return signalStoreFeature(
    withState<CustomerStoriesStateSlice>({
      stories: [],
      storiesLoading: false,
    }),
    withMethods((store, customerService = inject(CustomersService)) => ({
      loadStories: (customerId: number) => {
        patchState(store, { storiesLoading: true });
        return callApiMethod(() => customerService.getStory(customerId), {
          next: (data) =>
            patchState(store, { stories: data, storiesLoading: false }),
          error: () => patchState(store, { storiesLoading: false }),
        });
      },
    })),
  );
}

export interface CustomerInvoicingStateSlice {
  invoicesAndPayments: { type: 'invoice' | 'payment'; id: string }[];
  invoicesLoading: boolean;
  bankAccountsQuery: ApiQuery | null;
  bankAccounts: ICustomerBankAccount[];
  bankAccountsLoading: boolean;
}

export function withCustomerInvoicing() {
  return signalStoreFeature(
    withState<CustomerInvoicingStateSlice>({
      invoicesAndPayments: [],
      invoicesLoading: false,
      bankAccountsQuery: null,
      bankAccounts: [],
      bankAccountsLoading: false,
    }),
    withMethods(
      (
        store,
        invoicesService = inject(InvoicesService),
        customerBankAccountsService = inject(CustomerBankAccountsService),
      ) => ({
        loadInvoices: async (customerId: number) => {
          patchState(store, { invoicesLoading: true });
          return callApiMethod(
            () => invoicesService.getWithPayments(customerId),
            {
              next: (invoicesAndPayments) =>
                patchState(store, {
                  invoicesAndPayments,
                  invoicesLoading: false,
                }),
              error: () => patchState(store, { invoicesLoading: false }),
            },
          );
        },
        loadBankAccounts: async (
          customerId: number,
          query?: ApiQuery | null,
        ) => {
          patchState(store, {
            bankAccountsLoading: true,
            bankAccountsQuery: query ?? null,
          });

          const filters = apiFilter<ICustomerBankAccount>(
            query?.filters,
          ).customerId(customerId.toString());

          return callApiMethod(
            () =>
              customerBankAccountsService.query({
                ...query,
                filters,
                perPage: -1,
              }),
            {
              next: ({ data: bankAccounts }) =>
                patchState(store, {
                  bankAccounts,
                  bankAccountsLoading: false,
                }),
              error: () => patchState(store, { bankAccountsLoading: false }),
            },
          );
        },
        deleteBankAccount: async (bankAccountId: string) => {
          patchState(store, { bankAccountsLoading: true });

          return callApiMethod(
            () => customerBankAccountsService.delete(bankAccountId),
            {
              next: () =>
                patchState(store, (state) => ({
                  bankAccounts: state.bankAccounts.map((ba) =>
                    ba.id === bankAccountId
                      ? new CustomerBankAccount({
                          ...ba,
                          deletedAt: new Date(),
                        })
                      : ba,
                  ),
                  bankAccountsLoading: false,
                })),
              error: () => patchState(store, { bankAccountsLoading: false }),
            },
          );
        },
        restoreBankAccount: async (bankAccountId: string) => {
          patchState(store, { bankAccountsLoading: true });

          return callApiMethod(
            () => customerBankAccountsService.restore(bankAccountId),
            {
              next: (restoredBankAccount) =>
                patchState(store, (state) => ({
                  bankAccounts: state.bankAccounts.map((ba) =>
                    ba.id === bankAccountId
                      ? new CustomerBankAccount({
                          ...ba,
                          ...restoredBankAccount,
                        })
                      : ba,
                  ),
                  bankAccountsLoading: false,
                })),
              error: () => patchState(store, { bankAccountsLoading: false }),
            },
          );
        },
      }),
    ),
  );
}

export interface CustomerInstallationsStateSlice {
  installations: Task[];
  installationsLoading: boolean;
}

export function withCustomerInstallations() {
  return signalStoreFeature(
    withState<CustomerInstallationsStateSlice>({
      installations: [],
      installationsLoading: false,
    }),
    withComputed(({ installations }) => ({
      lastInstallation: computed(
        () =>
          installations()?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )?.[0] ?? null,
      ),
    })),
    withMethods((store, service = inject(CustomersService)) => ({
      loadInstallations: async (customerId: number, apId: string) => {
        patchState(store, { installationsLoading: true });
        try {
          const installations = await firstValueFrom(
            service.getInstallations(customerId, apId),
          );
          patchState(store, {
            installations,
            installationsLoading: false,
          });
        } catch (error) {
          patchState(store, {
            installations: [],
            installationsLoading: false,
          });

          throw error;
        }
      },
    })),
  );
}

export interface CustomerActivitiesStateSlice {
  activitiesSource: CustomerActivitySource;
  activities: CustomerActivity[];
  activitiesLoading: boolean;
}

export const CustomersStore = signalStore(
  { protectedState: false },
  withResourceState(CustomersService),
  withExport(CustomersService),
  withModelRestore(CustomersService),
  withCustomerAccessPoints(CustomersService),
  withCustomerInstallations(),
  withCustomerStories(),
  withCustomerInvoicing(),
  withMethods((store, service = inject(CustomersService)) => ({
    async exportServices(query?: ApiQuery | null) {
      patchState(store, setLoading());

      try {
        await service.exportServices(query ?? store.query());
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    async exportDevices(query?: ApiQuery | null) {
      patchState(store, setLoading());

      try {
        await service.exportDevices(query ?? store.query());
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    async exportFees(query?: ApiQuery | null) {
      patchState(store, setLoading());

      try {
        await service.exportFees(query ?? store.query());
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    async exportMaterials(query?: ApiQuery | null) {
      patchState(store, setLoading());

      try {
        await service.exportMaterials(query ?? store.query());
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    async exportAdditionalProducts(query?: ApiQuery | null) {
      patchState(store, setLoading());

      try {
        await service.exportAdditionalProducts(query ?? store.query());
        patchState(store, setLoaded());
      } catch (error) {
        patchState(store, setError(error));
      }
    },

    batchAction(data: ICustomerBatchActionData) {
      patchState(store, setLoading());
      return callApiMethod(() => service.batchAction(data, store.query()), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },

    action(customerId: number, data: Partial<ICustomerActionData>) {
      patchState(store, setLoading());
      return callApiMethod(
        () =>
          service.action(customerId, {
            customerId,
            ...data,
          }),
        {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        },
      );
    },

    bindTag(customerId: number, tagId: string) {
      patchState(store, setLoading());
      return callApiMethod(() => service.bindTag(customerId, tagId), {
        next: (data) =>
          patchState(
            store,
            {
              model: plainToInstance(Customer, {
                ...store.model(),
                tags: data.tags,
              } as Customer),
            },
            setLoaded(),
          ),
        error: () => patchState(store, { accessPointsLoading: false }),
      });
    },
    unbindTag(customerId: number, tagId: string) {
      patchState(store, setLoading());
      return callApiMethod(() => service.unbindTag(customerId, tagId), {
        next: (data) =>
          patchState(
            store,
            {
              model: plainToInstance(Customer, {
                ...store.model(),
                tags: data.tags,
              } as Customer),
            },
            setLoaded(),
          ),
        error: () => patchState(store, { accessPointsLoading: false }),
      });
    },

    moveServicesToAnotherAp(
      customerId: number,
      apId: string,
      newApId: string,
      serviceId?: string,
    ) {
      patchState(store, setLoading());
      return callApiMethod(
        () =>
          service.moveServicesToAnotherAp(customerId, apId, newApId, serviceId),
        {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        },
      );
    },

    setInvoiceBalance(customerId: number, data: SetInvoiceBalanceData) {
      patchState(store, setLoading());
      return callApiMethod(() => service.setInvoiceBalance(customerId, data), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },

    setInvoiceFrom(customerId: number, date: Date | null) {
      patchState(store, setLoading());
      return callApiMethod(() => service.setInvoiceFrom(customerId, date), {
        next: () => patchState(store, setLoaded()),
        error: (err) => patchState(store, setError(err)),
      });
    },

    setSipoInvoicingState(customerId: number, data: SetSipoInvoiceStateData) {
      patchState(store, setLoading());
      return callApiMethod(
        () => service.setSipoInvoicingState(customerId, data),
        {
          next: () => patchState(store, setLoaded()),
          error: (err) => patchState(store, setError(err)),
        },
      );
    },
  })),
);

// export interface CustomerState {
//   customerActiveId: number | null;
//   customer: Customer | null;
//   customerLoading: boolean;

//   accessPointActiveId: string | null;
//   accessPoints: AccessPoint[];
//   accessPointsLoading: boolean;

//   serviceActiveId: string | null;
//   serviceQuery: {
//     withDeleted: boolean;
//   };
//   services: Service[];
//   servicesLoading: boolean;

//   fees: CustomerFee[];
//   feesLoading: boolean;

//   comments: Comment[];
//   commentsLoading: boolean;
// }

// @Injectable()
// export class CustomerStore {
//   readonly #customersService = inject(CustomersService);
//   readonly #accessPointsService = inject(AccessPointsService);
//   readonly #servicesService = inject(ServicesService);
//   readonly #feesService = inject(FeesService);
//   readonly #commentsService = inject(CommentsService);

//   readonly #state = signalState<CustomerState>({
//     customerActiveId: null,
//     customer: null,
//     customerLoading: false,
//     accessPointActiveId: null,
//     accessPoints: [],
//     accessPointsLoading: false,
//     serviceActiveId: null,
//     serviceQuery: {
//       withDeleted: false,
//     },
//     services: [],
//     servicesLoading: false,
//     fees: [],
//     feesLoading: false,
//     comments: [],
//     commentsLoading: false,
//   });

//   readonly state = this.#state;

//   async loadCustomer(customerId: number) {
//     patchState(this.#state, {
//       customerLoading: true,
//       customerActiveId: customerId,
//     });
//     try {
//       const customer = await firstValueFrom(
//         this.#customersService.findOne(customerId),
//       );
//       patchState(this.#state, { customer });
//       return customer;
//     } finally {
//       patchState(this.#state, { customerLoading: false });
//     }
//   }

//   setActiveAccessPoint(id: string | null) {
//     patchState(this.#state, { accessPointActiveId: id });
//   }

//   async loadAccessPoints() {
//     const customerId = this.#state.customerActiveId();

//     if (!customerId) {
//       return;
//     }

//     patchState(this.#state, { accessPointsLoading: true });
//     try {
//       const data = await firstValueFrom(
//         this.#accessPointsService.query({
//           filters: `customerId eq ${customerId}`,
//           perPage: -1,
//           sort: ['address'],
//         }),
//       );
//       patchState(this.#state, { accessPoints: data.data });
//     } finally {
//       patchState(this.#state, { accessPointsLoading: false });
//     }
//   }

//   setActiveService(id: string | null) {
//     patchState(this.#state, { serviceActiveId: id });
//   }

//   async loadServices() {
//     const accessPointId = this.#state.accessPointActiveId();
//     const { withDeleted } = this.#state.serviceQuery();

//     if (!accessPointId) {
//       return;
//     }

//     patchState(this.#state, { servicesLoading: true });

//     try {
//       const services = await firstValueFrom(
//         this.#servicesService.getServices(accessPointId, withDeleted),
//       );
//       patchState(this.#state, { services });
//     } finally {
//       patchState(this.#state, { servicesLoading: false });
//     }
//   }

//   async loadFees() {
//     const accessPointId = this.#state.accessPointActiveId();

//     if (!accessPointId) {
//       return;
//     }

//     patchState(this.#state, { feesLoading: true });

//     try {
//       const fees = await firstValueFrom(
//         this.#customersService.getFees(accessPointId),
//       );
//       patchState(this.#state, { fees });
//     } finally {
//       patchState(this.#state, { feesLoading: false });
//     }
//   }
// }
